import { Badge, Banner, Button, Dialog, Field, Input, Switch } from '@cloudflare/kumo';
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAppToast } from '../hooks/useAppToast';
import { AppTabs as Tabs } from '../components/ui/AppTabs';
import { ICONS, glyph } from '../components/ui/icons';
import { PasswordField } from '../components/ui/PasswordField';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { palette } from '../lib/palette';
import { PerfexSyncModal } from '../components/PerfexSyncModal';
import { OrganizationGeneralTab } from '../components/OrganizationGeneralTab';
import { useAuth } from '../contexts/AuthContext';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useSearchParams } from 'react-router-dom';
import { SkeletonText } from '../components/ui/Skeleton';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Settings {
  id: string;
  openaiApiKey?: string;
  groqApiKey?: string;
  chatwootUrl?: string;
  chatwootAccountId?: string;
  chatwootApiToken?: string;
  perfexUrl?: string;
  perfexToken?: string;
  emailNotificationsEnabled?: boolean;
  smtpHost?: string;
  smtpPort?: number;
  smtpUser?: string;
  smtpPassword?: string;
  smtpFrom?: string;
}

const settingsSchema = z.object({
  openaiApiKey: z.string().optional(),
  groqApiKey: z.string().optional(),
  chatwootUrl: z.string().optional(),
  chatwootAccountId: z.string().optional(),
  chatwootApiToken: z.string().optional(),
  perfexUrl: z.string().optional(),
  perfexToken: z.string().optional(),
  smtpHost: z.string().optional(),
  smtpPort: z.string().optional(),
  smtpUser: z.string().optional(),
  smtpPassword: z.string().optional(),
  smtpFrom: z.string().optional(),
});

type SettingsFormData = z.infer<typeof settingsSchema>;
type ActiveModal = 'openai' | 'groq' | 'chatwoot' | 'perfex' | 'email' | null;

// ─── Integrações ─────────────────────────────────────────────────────────────

type IntegrationId = 'openai' | 'groq' | 'chatwoot' | 'perfex' | 'email';

// Ícone Cloudscape sobre o tom do grupo (mesma linguagem dos blocos do Flow Builder)
const INTEGRATIONS: Record<IntegrationId, { name: string; description: string; glyph: string; accent: string; tint: string }> = {
  openai:   { name: 'OpenAI',   description: 'GPT-4o, GPT-4 Turbo e GPT-3.5 nos blocos de IA',        glyph: 'Sparkle',       accent: palette.purple.base, tint: palette.purple.light3 },
  groq:     { name: 'Groq',     description: 'LLaMA 3.1, Mixtral e Gemma2, com respostas rápidas',    glyph: 'LightningBolt', accent: palette.purple.base, tint: palette.purple.light3 },
  chatwoot: { name: 'Chatwoot', description: 'Sincroniza as conversas do WhatsApp com a caixa de entrada', glyph: 'QuestionMarkWithCircle', accent: palette.blue.base, tint: palette.blue.light3 },
  perfex:   { name: 'Perfex CRM', description: 'Importa clientes e leads do Perfex para as campanhas', glyph: 'Building',      accent: palette.blue.base,   tint: palette.blue.light3 },
  email:    { name: 'E-mail (SMTP)', description: 'Alertas de campanha com falha, sessão desconectada e cota', glyph: 'Email', accent: palette.green.dark1, tint: palette.green.light3 },
};

const modalFormGap: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

function IntegrationIcon({ id, size = 40 }: { id: IntegrationId; size?: number }) {
  const it = INTEGRATIONS[id];
  return (
    <div
      aria-hidden
      style={{
        width: size, height: size, borderRadius: 8, flexShrink: 0,
        background: it.tint, color: it.accent,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {glyph(it.glyph, 16)}
    </div>
  );
}

function ModalTitle({ id, title, children }: { id: IntegrationId; title: string; children: React.ReactNode }) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <IntegrationIcon id={id} size={36} />
        <h3 className="text-lg font-semibold" style={{ fontSize: 18 }}>{title}</h3>
      </div>
      <span className="text-xs text-kumo-subtle" style={{ marginBottom: 20 }}>{children}</span>
    </>
  );
}

type IntegrationCardProps = {
  id: IntegrationId;
  isConfigured: boolean;
  onConfigure: () => void;
  onRemove?: () => void;
};

function IntegrationCard({ id, isConfigured, onConfigure, onRemove }: IntegrationCardProps) {
  const it = INTEGRATIONS[id];
  return (
    <div className="settings-card">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <IntegrationIcon id={id} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div className="font-semibold" style={{ fontSize: 15, color: palette.gray.dark3 }}>{it.name}</div>
            {isConfigured
              ? <Badge variant="green">Configurado</Badge>
              : <Badge variant="neutral">Não configurado</Badge>}
          </div>
          <span className="text-xs text-kumo-subtle" style={{ marginTop: 4 }}>{it.description}</span>
        </div>
      </div>
      <div className="settings-card__actions">
        <Button variant="secondary" icon={isConfigured ? ICONS.Edit : ICONS.Plus} onClick={onConfigure}>
          {isConfigured ? 'Editar' : 'Conectar'}
        </Button>
        {isConfigured && onRemove && (
          <Button variant="ghost" shape="square" icon={ICONS.Trash} aria-label={`Remover ${it.name}`} title={`Remover ${it.name}`} onClick={onRemove} />
        )}
      </div>
    </div>
  );
}

function SettingsSection({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 32 }}>
      <h4 className="text-base font-semibold" style={{ fontSize: 16 }}>{title}</h4>
      <span className="text-xs text-kumo-subtle" style={{ marginTop: 2, marginBottom: 12 }}>{description}</span>
      <div className="settings-grid">{children}</div>
    </section>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function SettingsPage() {
  const toast = useAppToast();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<IntegrationId | null>(null);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const settingsTab = searchParams.get('aba') === 'integracoes' ? 'integracoes' : 'geral';

  const authenticatedFetch = useAuthenticatedFetch();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SettingsFormData>({ resolver: zodResolver(settingsSchema) });

  useEffect(() => {
    loadSettings();
    const handleTenantChange = () => loadSettings();
    window.addEventListener('superadmin-tenant-changed', handleTenantChange);
    return () => window.removeEventListener('superadmin-tenant-changed', handleTenantChange);
  }, [user]);

  const loadSettings = async () => {
    try {
      let url = '/api/settings';
      if (user?.role === 'SUPERADMIN') {
        const selectedTenantId = localStorage.getItem('selected_tenant_id');
        if (selectedTenantId) url = `/api/settings?tenantId=${selectedTenantId}`;
      }
      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setValue('openaiApiKey', data.openaiApiKey || '');
        setValue('groqApiKey', data.groqApiKey || '');
        setValue('chatwootUrl', data.chatwootUrl || '');
        setValue('chatwootAccountId', data.chatwootAccountId || '');
        setValue('chatwootApiToken', data.chatwootApiToken || '');
        setValue('perfexUrl', data.perfexUrl || '');
        setValue('perfexToken', data.perfexToken || '');
        setValue('smtpHost', data.smtpHost || '');
        setValue('smtpPort', data.smtpPort ? String(data.smtpPort) : '');
        setValue('smtpUser', data.smtpUser || '');
        setValue('smtpPassword', data.smtpPassword || '');
        setValue('smtpFrom', data.smtpFrom || '');
        setEmailEnabled(data.emailNotificationsEnabled ?? false);
      }
    } catch {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: SettingsFormData) => {
    try {
      // Strip SMTP fields — those are managed exclusively by saveEmailConfig
      const { smtpHost: _sh, smtpPort: _sp, smtpUser: _su, smtpPassword: _spw, smtpFrom: _sf, ...rest } = data;
      let requestData: Omit<SettingsFormData, 'smtpHost' | 'smtpPort' | 'smtpUser' | 'smtpPassword' | 'smtpFrom'> & { tenantId?: string } = rest;
      if (user?.role === 'SUPERADMIN') {
        const selectedTenantId = localStorage.getItem('selected_tenant_id');
        if (selectedTenantId) requestData = { ...rest, tenantId: selectedTenantId };
      }
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(requestData),
      });
      if (response.ok) {
        toast.success('Configurações salvas com sucesso');
        setActiveModal(null);
        await loadSettings();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao salvar configurações');
      }
    } catch {
      toast.error('Erro ao salvar configurações');
    }
  };

  const removeIntegration = async (type: 'openai' | 'groq' | 'chatwoot' | 'perfex') => {
    const names = { openai: 'OpenAI', groq: 'Groq', chatwoot: 'Chatwoot', perfex: 'Perfex CRM' };
    try {
      const requestData: Record<string, string> = {};
      if (type === 'openai') requestData.openaiApiKey = '';
      else if (type === 'groq') requestData.groqApiKey = '';
      else if (type === 'chatwoot') { requestData.chatwootUrl = ''; requestData.chatwootAccountId = ''; requestData.chatwootApiToken = ''; }
      else if (type === 'perfex') { requestData.perfexUrl = ''; requestData.perfexToken = ''; }
      if (user?.role === 'SUPERADMIN') {
        const tid = localStorage.getItem('selected_tenant_id');
        if (tid) requestData.tenantId = tid;
      }
      const response = await authenticatedFetch('/api/settings', { method: 'PUT', body: JSON.stringify(requestData) });
      if (response.ok) {
        toast.success(`Integração com ${names[type]} removida`);
        setActiveModal(null);
        await loadSettings();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Erro ao remover integração');
      }
    } catch {
      toast.error('Erro ao remover integração');
    }
  };

  const saveEmailConfig = async (data: SettingsFormData) => {
    setSavingEmail(true);
    try {
      const payload: Record<string, unknown> = {
        smtpHost: data.smtpHost || '',
        smtpPort: data.smtpPort ? Number(data.smtpPort) : null,
        smtpUser: data.smtpUser || '',
        smtpPassword: data.smtpPassword || '',
        smtpFrom: data.smtpFrom || '',
        emailNotificationsEnabled: emailEnabled,
      };
      if (user?.role === 'SUPERADMIN') {
        const tid = localStorage.getItem('selected_tenant_id');
        if (tid) payload.tenantId = tid;
      }
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        toast.success('Configurações de e-mail salvas');
        setActiveModal(null);
        await loadSettings();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Erro ao salvar configurações de e-mail');
      }
    } catch {
      toast.error('Erro ao salvar configurações de e-mail');
    } finally {
      setSavingEmail(false);
    }
  };

  const disableEmail = async () => {
    try {
      const payload: Record<string, unknown> = { emailNotificationsEnabled: false, smtpHost: '', smtpUser: '', smtpPassword: '', smtpFrom: '' };
      if (user?.role === 'SUPERADMIN') {
        const tid = localStorage.getItem('selected_tenant_id');
        if (tid) payload.tenantId = tid;
      }
      const response = await authenticatedFetch('/api/settings', { method: 'PUT', body: JSON.stringify(payload) });
      if (response.ok) { toast.success('Notificações por e-mail desativadas'); await loadSettings(); }
      else { const err = await response.json(); toast.error(err.error || 'Erro ao desativar'); }
    } catch { toast.error('Erro ao desativar notificações'); }
  };

  const confirmRemoval = async () => {
    const id = pendingRemoval;
    setPendingRemoval(null);
    if (!id) return;
    if (id === 'email') await disableEmail();
    else await removeIntegration(id);
  };

  // ─── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ padding: '28px 40px', minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <SkeletonText lines={5} label="Carregando configurações" />
      </div>
    );
  }

  const hasOpenAI   = !!settings?.openaiApiKey;
  const hasGroq     = !!settings?.groqApiKey;
  const hasChatwoot = !!(settings?.chatwootUrl && settings?.chatwootAccountId && settings?.chatwootApiToken);
  const hasPerfex   = !!(settings?.perfexUrl && settings?.perfexToken);
  const hasEmail    = !!(settings?.emailNotificationsEnabled && settings?.smtpHost && settings?.smtpUser);

  // ─── Render ─────────────────────────────────────────────────────────────────

  const integrationsTabContent = (
    <>
      <SettingsSection title="Inteligência artificial" description="Chaves de API usadas pelos blocos de IA das campanhas interativas.">
        <IntegrationCard id="openai" isConfigured={hasOpenAI} onConfigure={() => setActiveModal('openai')} onRemove={() => setPendingRemoval('openai')} />
        <IntegrationCard id="groq" isConfigured={hasGroq} onConfigure={() => setActiveModal('groq')} onRemove={() => setPendingRemoval('groq')} />
      </SettingsSection>

      <SettingsSection title="Atendimento e CRM" description="Sincronize conversas, contatos e leads com as ferramentas da sua equipe.">
        <IntegrationCard id="chatwoot" isConfigured={hasChatwoot} onConfigure={() => setActiveModal('chatwoot')} onRemove={() => setPendingRemoval('chatwoot')} />
        <IntegrationCard id="perfex" isConfigured={hasPerfex} onConfigure={() => setActiveModal('perfex')} onRemove={() => setPendingRemoval('perfex')} />
      </SettingsSection>

      <SettingsSection title="Notificações" description="Alertas automáticos por e-mail para os administradores da organização.">
        <IntegrationCard id="email" isConfigured={hasEmail} onConfigure={() => setActiveModal('email')} onRemove={() => setPendingRemoval('email')} />
      </SettingsSection>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: palette.gray.dark1 }}>
        <ICONS.Lock size={16} />
        <span className="text-xs text-kumo-subtle">As chaves e senhas ficam só no servidor e nunca aparecem nos logs.</span>
      </div>
    </>
  );

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '28px 40px 0', marginBottom: 24 }}>
        <div>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Configurações</h2>
          <div style={{ color: palette.gray.dark1 }}>Dados e integrações desta organização</div>
        </div>
      </div>

      <div className="page-content">
        {/* Abas como no "Project Settings" do Atlas: Geral | Integrações */}
        <Tabs
          activeTabId={settingsTab}
          onChange={({ detail }) => setSearchParams({ aba: detail.activeTabId === 'integracoes' ? 'integracoes' : 'geral' }, { replace: true })}
          tabs={[
            { id: 'geral', label: 'Geral', content: <OrganizationGeneralTab /> },
            { id: 'integracoes', label: `Integrações (${[hasOpenAI, hasGroq, hasChatwoot, hasPerfex, hasEmail].filter(Boolean).length}/5)`, content: integrationsTabContent },
          ]}
        />
      </div>

      <DangerConfirmModal
        open={pendingRemoval !== null}
        onConfirm={confirmRemoval}
        onCancel={() => setPendingRemoval(null)}
        title={pendingRemoval === 'email' ? 'Desativar notificações por e-mail?' : `Remover ${pendingRemoval ? INTEGRATIONS[pendingRemoval].name : ''}?`}
        confirmText={pendingRemoval === 'email' ? 'Desativar' : 'Remover'}
        objectName={pendingRemoval && pendingRemoval !== 'email' ? INTEGRATIONS[pendingRemoval].name : null}
      >
        {pendingRemoval === 'email'
          ? 'Os alertas deixam de ser enviados e os dados do servidor SMTP são apagados.'
          : 'As credenciais são apagadas e os recursos que dependem desta integração param de funcionar.'}
      </DangerConfirmModal>

      {/* ── Modal OpenAI ───────────────────────────────────────────────────── */}
      <Dialog.Root open={activeModal === 'openai'} onOpenChange={(o: boolean) => { if (!o) (() => { if (activeModal === 'openai') setActiveModal(null); })(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<ModalTitle id="openai" title="Configurar OpenAI">Chave de API para integração com ChatGPT e GPT-4 nos nós de campanha.</ModalTitle>}</Dialog.Title>

        <form onSubmit={handleSubmit(onSubmit)} style={modalFormGap}>
          <Controller
            name="openaiApiKey"
            control={control}
            render={({ field }) => (
              <PasswordField
                label="API Key"
                errorText={errors.openaiApiKey?.message}
                value={field.value ?? ''}
                onChange={field.onChange}
                placeholder="sk-proj-..."
              />
            )}
          />
          <Banner variant="default" description={<>Encontre sua chave em <strong>platform.openai.com → API keys</strong>.</>} />
          {errors.root && <Banner variant="error" description={<>{errors.root.message}</>} />}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <Button variant="secondary" type="button" onClick={() => setActiveModal(null)}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={isSubmitting} loading={isSubmitting}>Salvar</Button>
          </div>
        </form>
      
</Dialog>
</Dialog.Root>

      {/* ── Modal Groq ─────────────────────────────────────────────────────── */}
      <Dialog.Root open={activeModal === 'groq'} onOpenChange={(o: boolean) => { if (!o) (() => { if (activeModal === 'groq') setActiveModal(null); })(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<ModalTitle id="groq" title="Configurar Groq">Chave de API para modelos ultra-rápidos: LLaMA 3.1, Mixtral, Gemma2.</ModalTitle>}</Dialog.Title>

        <form onSubmit={handleSubmit(onSubmit)} style={modalFormGap}>
          <Controller
            name="groqApiKey"
            control={control}
            render={({ field }) => (
              <PasswordField
                label="API Key"
                errorText={errors.groqApiKey?.message}
                value={field.value ?? ''}
                onChange={field.onChange}
                placeholder="gsk_..."
              />
            )}
          />
          <Banner variant="default" description={<>Encontre sua chave em <strong>console.groq.com → API Keys</strong>. O plano gratuito já inclui uma cota generosa.</>} />
          {errors.root && <Banner variant="error" description={<>{errors.root.message}</>} />}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <Button variant="secondary" type="button" onClick={() => setActiveModal(null)}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={isSubmitting} loading={isSubmitting}>Salvar</Button>
          </div>
        </form>
      
</Dialog>
</Dialog.Root>

      {/* ── Modal Chatwoot ─────────────────────────────────────────────────── */}
      <Dialog.Root open={activeModal === 'chatwoot'} onOpenChange={(o: boolean) => { if (!o) (() => { if (activeModal === 'chatwoot') setActiveModal(null); })(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<ModalTitle id="chatwoot" title="Configurar Chatwoot">Conecte sua instância Chatwoot para sincronizar conversas WhatsApp.</ModalTitle>}</Dialog.Title>

        <form onSubmit={handleSubmit(onSubmit)} style={modalFormGap}>
          <Field label="URL do Chatwoot" description="URL completa da sua instância (sem barra no final)" error={errors.chatwootUrl?.message ? { message: errors.chatwootUrl?.message, match: true } : undefined}>
            <Controller
              name="chatwootUrl"
              control={control}
              render={({ field }) => (
                <Input onChange={(e) => field.onChange(e.target.value)} value={field.value ?? ''} placeholder="https://app.chatwoot.com" />
              )}
            />
          </Field>
          <Field label="ID da Conta" description="Número do ID da conta — visível na URL ao fazer login" error={errors.chatwootAccountId?.message ? { message: errors.chatwootAccountId?.message, match: true } : undefined}>
            <Controller
              name="chatwootAccountId"
              control={control}
              render={({ field }) => (
                <Input onChange={(e) => field.onChange(e.target.value)} value={field.value ?? ''} placeholder="123456" />
              )}
            />
          </Field>
          <Controller
            name="chatwootApiToken"
            control={control}
            render={({ field }) => (
              <PasswordField
                label="Token de API"
                errorText={errors.chatwootApiToken?.message}
                value={field.value ?? ''}
                onChange={field.onChange}
                placeholder="••••••••••••••••"
              />
            )}
          />
          <span className="text-xs text-kumo-subtle" style={{ marginTop: -8 }}>
            O token fica em Configurações do Chatwoot → Perfil → Token de acesso.
          </span>
          {errors.root && <Banner variant="error" description={<>{errors.root.message}</>} />}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <Button variant="secondary" type="button" onClick={() => setActiveModal(null)}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={isSubmitting} loading={isSubmitting}>Salvar</Button>
          </div>
        </form>
      
</Dialog>
</Dialog.Root>

      {/* ── Modal E-mail ───────────────────────────────────────────────────── */}
      <Dialog.Root open={activeModal === 'email'} onOpenChange={(o: boolean) => { if (!o) (() => { if (activeModal === 'email') setActiveModal(null); })(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<ModalTitle id="email" title="Notificações por e-mail">
            Configure um servidor SMTP para receber alertas quando campanhas falharem, sessões forem desconectadas ou quotas atingirem o limite.
          </ModalTitle>}</Dialog.Title>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderBottom: `1px solid ${palette.gray.light2}`, marginBottom: 16 }}>
          <div>
            <div className="font-semibold" id="email-toggle-label">Ativar notificações por e-mail</div>
            <span className="text-xs text-kumo-subtle">Envia alertas para os administradores da organização</span>
          </div>
          <Switch onCheckedChange={(checked: boolean) => setEmailEnabled(checked)} aria-label="Ativar notificações por e-mail" checked={emailEnabled} />
        </div>

        <form onSubmit={handleSubmit(saveEmailConfig)} style={modalFormGap}>
          <Field label="Servidor SMTP" description="Host do servidor SMTP" error={errors.smtpHost?.message ? { message: errors.smtpHost?.message, match: true } : undefined}>
            <Controller
              name="smtpHost"
              control={control}
              render={({ field }) => (
                <Input onChange={(e) => field.onChange(e.target.value)} value={field.value ?? ''} placeholder="smtp.gmail.com" />
              )}
            />
          </Field>
          <Field label="Porta" description="465 para SSL, 587 para TLS/STARTTLS" error={errors.smtpPort?.message ? { message: errors.smtpPort?.message, match: true } : undefined}>
            <Controller
              name="smtpPort"
              control={control}
              render={({ field }) => (
                <Input onChange={(e) => field.onChange(e.target.value)} value={field.value ?? ''} placeholder="587" />
              )}
            />
          </Field>
          <Field label="Usuário" error={errors.smtpUser?.message ? { message: errors.smtpUser?.message, match: true } : undefined}>
            <Controller
              name="smtpUser"
              control={control}
              render={({ field }) => (
                <Input onChange={(e) => field.onChange(e.target.value)} value={field.value ?? ''} placeholder="seu@email.com" />
              )}
            />
          </Field>
          <Controller
            name="smtpPassword"
            control={control}
            render={({ field }) => (
              <PasswordField
                label="Senha"
                errorText={errors.smtpPassword?.message}
                value={field.value ?? ''}
                onChange={field.onChange}
                placeholder="••••••••••••"
              />
            )}
          />
          <Field label="Remetente (From)" description="Deixe em branco para usar o usuário SMTP" error={errors.smtpFrom?.message ? { message: errors.smtpFrom?.message, match: true } : undefined}>
            <Controller
              name="smtpFrom"
              control={control}
              render={({ field }) => (
                <Input onChange={(e) => field.onChange(e.target.value)} value={field.value ?? ''} placeholder="Ingrave Argentic <noreply@empresa.com>" />
              )}
            />
          </Field>
          <Banner variant="default" description={<>
            No Gmail, use uma <strong>senha de app</strong>, não a senha normal: ative a verificação em duas etapas e crie a senha em <strong>Conta Google → Segurança</strong>.
          </>} />
          {errors.root && <Banner variant="error" description={<>{errors.root.message}</>} />}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <Button variant="secondary" type="button" onClick={() => setActiveModal(null)}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={savingEmail} loading={savingEmail}>Salvar</Button>
          </div>
        </form>
      
</Dialog>
</Dialog.Root>

      {/* ── Modal Perfex CRM ───────────────────────────────────────────────── */}
      <PerfexSyncModal
        isOpen={activeModal === 'perfex'}
        onClose={() => setActiveModal(null)}
        perfexUrl={settings?.perfexUrl || ''}
        perfexToken={settings?.perfexToken || ''}
        onSave={async (url, token) => {
          try {
            const requestData: Record<string, string> = { perfexUrl: url, perfexToken: token };
            if (user?.role === 'SUPERADMIN') {
              const tid = localStorage.getItem('selected_tenant_id');
              if (tid) requestData.tenantId = tid;
            }
            const response = await authenticatedFetch('/api/settings', {
              method: 'PUT',
              body: JSON.stringify(requestData),
            });
            if (response.ok) {
              toast.success('Integração com Perfex CRM configurada');
              await loadSettings();
            } else {
              const err = await response.json();
              toast.error(err.error || 'Erro ao configurar Perfex CRM');
            }
          } catch {
            toast.error('Erro ao configurar Perfex CRM');
          }
        }}
      />
    </>
  );
}
